import React, { useState } from 'react';
import { X, Plus, Minus, Trash2, Send, ArrowLeft, CheckCircle } from 'lucide-react';
import { useQuoteCart } from '@/context/QuoteCartContext';
import { publicApi } from '@/services/api';
import { toast } from 'sonner';
import { resolveImage, PRODUCT_IMAGE_BASE } from '@/lib/imageUrl';

const QuoteDrawerV2 = () => {
  const { items, isOpen, setIsOpen, updateQuantity, updateNotes, removeItem, clearCart } = useQuoteCart();
  const [step, setStep] = useState('cart');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [folio, setFolio] = useState('');
  const [formData, setFormData] = useState({
    cliente_nombre: '',
    cliente_empresa: '',
    cliente_email: '',
    cliente_telefono: '',
    cliente_ciudad: '',
    comentarios: ''
  });

  const validateForm = () => {
    const newErrors = {};
    if (!formData.cliente_nombre.trim()) newErrors.cliente_nombre = 'El nombre es requerido';
    if (!formData.cliente_email.trim()) newErrors.cliente_email = 'El email es requerido';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.cliente_email)) newErrors.cliente_email = 'Ingresa un email válido';
    if (!formData.cliente_telefono.trim()) newErrors.cliente_telefono = 'El teléfono es requerido';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFieldChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) setErrors({ ...errors, [field]: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      const response = await publicApi.createQuote({
        ...formData,
        productos: items.map(item => ({
          product_id: item.product_id,
          nombre: item.nombre,
          codigo: item.codigo || '',
          variacion: item.variacion || '',
          cantidad: item.cantidad,
          notas: item.notas || ''
        }))
      });
      setFolio(response.data.folio);
      setStep('success');
      clearCart();
    } catch (error) {
      toast.error('Error al enviar la cotización');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setTimeout(() => {
      setStep('cart');
      setFormData({ cliente_nombre: '', cliente_empresa: '', cliente_email: '', cliente_telefono: '', cliente_ciudad: '', comentarios: '' });
      setErrors({});
    }, 300);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50" onClick={handleClose} />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-v2-dark z-50 shadow-2xl animate-slide-in-right flex flex-col border-l border-v2-orange/30">
        {/* Header */}
        <div className="bg-v2-darker p-5 flex items-center justify-between border-b border-v2-border">
          <div>
            <h2 className="text-white font-bold text-xl uppercase tracking-wider">
              {step === 'cart' && 'Cotizador'}
              {step === 'form' && 'Tus Datos'}
              {step === 'success' && 'Enviado'}
            </h2>
            <div className="flex items-center gap-2 mt-1">
              <span className={`w-2 h-2 rounded-full ${step === 'cart' ? 'bg-v2-orange' : 'bg-v2-border'}`} />
              <span className={`w-2 h-2 rounded-full ${step === 'form' ? 'bg-v2-orange' : 'bg-v2-border'}`} />
              <span className={`w-2 h-2 rounded-full ${step === 'success' ? 'bg-v2-orange' : 'bg-v2-border'}`} />
            </div>
          </div>
          <button onClick={handleClose} className="w-10 h-10 bg-v2-light-gray hover:bg-v2-orange rounded flex items-center justify-center transition-colors" data-testid="close-drawer-btn">
            <X size={20} className="text-white" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {step === 'cart' && (
            <>
              {items.length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-24 h-24 bg-v2-light-gray rounded-full flex items-center justify-center mx-auto mb-6">
                    <svg className="w-12 h-12 text-v2-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                    </svg>
                  </div>
                  <h3 className="text-white font-bold text-xl mb-2">Tu cotizador está vacío</h3>
                  <p className="text-v2-text-muted">Agrega productos para solicitar una cotización</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {items.map((item, idx) => (
                    <div key={idx} className="bg-v2-gray border border-v2-border rounded-lg p-4 group hover:border-v2-orange/50 transition-colors" data-testid={`cart-item-${idx}`}>
                      <div className="flex gap-4">
                        <img 
                          src={resolveImage(item.ruta_img, PRODUCT_IMAGE_BASE)}
                          alt={item.nombre}
                          className="w-20 h-20 object-cover rounded flex-shrink-0"
                          onError={(e) => { e.target.src = 'https://placehold.co/100x100/1A1A1A/FF6B00?text=NA'; }}
                        />
                        <div className="flex-1 min-w-0">
                          <h4 className="font-bold text-white text-sm">{item.nombre}</h4>
                          {item.codigo && (
                            <div className="mt-1">
                              <span className="text-v2-orange font-mono text-xs font-bold bg-v2-orange/10 px-2 py-0.5 rounded">{item.codigo}</span>
                              <span className="text-v2-text-muted text-xs ml-2">{item.variacion}</span>
                            </div>
                          )}
                          
                          {/* Quantity */}
                          <div className="flex items-center gap-3 mt-3">
                            <button onClick={() => updateQuantity(idx, item.cantidad - 1)} className="w-7 h-7 bg-v2-light-gray hover:bg-v2-orange rounded flex items-center justify-center transition-colors">
                              <Minus size={14} className="text-white" />
                            </button>
                            <span className="w-8 text-center font-bold text-white text-sm">{item.cantidad}</span>
                            <button onClick={() => updateQuantity(idx, item.cantidad + 1)} className="w-7 h-7 bg-v2-light-gray hover:bg-v2-orange rounded flex items-center justify-center transition-colors">
                              <Plus size={14} className="text-white" />
                            </button>
                            <button onClick={() => removeItem(idx)} className="ml-auto w-7 h-7 text-v2-text-muted hover:text-red-500 hover:bg-red-500/10 rounded flex items-center justify-center transition-colors">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      </div>
                      
                      {/* Notes */}
                      <input
                        type="text"
                        placeholder="Notas adicionales..."
                        value={item.notas || ''}
                        onChange={(e) => updateNotes(idx, e.target.value)}
                        className="mt-3 w-full px-3 py-2 bg-v2-darker border border-v2-border rounded text-white placeholder-v2-text-muted focus:border-v2-orange focus:outline-none text-sm transition-colors"
                      />
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {step === 'form' && (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="quote-form" noValidate>
              <div>
                <label className="block text-sm font-medium text-v2-text-muted mb-2">Nombre completo *</label>
                <input type="text" value={formData.cliente_nombre} onChange={(e) => handleFieldChange('cliente_nombre', e.target.value)}
                  className={`w-full px-4 py-3 bg-v2-gray border rounded text-white placeholder-v2-text-muted focus:border-v2-orange focus:outline-none transition-colors ${errors.cliente_nombre ? 'border-red-500' : 'border-v2-border'}`}
                  data-testid="input-nombre" />
                {errors.cliente_nombre && <p className="text-red-400 text-xs mt-1">{errors.cliente_nombre}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-v2-text-muted mb-2">Empresa (opcional)</label>
                <input type="text" value={formData.cliente_empresa} onChange={(e) => handleFieldChange('cliente_empresa', e.target.value)}
                  className="w-full px-4 py-3 bg-v2-gray border border-v2-border rounded text-white placeholder-v2-text-muted focus:border-v2-orange focus:outline-none transition-colors"
                  data-testid="input-empresa" />
              </div>
              <div>
                <label className="block text-sm font-medium text-v2-text-muted mb-2">Email *</label>
                <input type="email" value={formData.cliente_email} onChange={(e) => handleFieldChange('cliente_email', e.target.value)}
                  className={`w-full px-4 py-3 bg-v2-gray border rounded text-white placeholder-v2-text-muted focus:border-v2-orange focus:outline-none transition-colors ${errors.cliente_email ? 'border-red-500' : 'border-v2-border'}`}
                  data-testid="input-email" />
                {errors.cliente_email && <p className="text-red-400 text-xs mt-1">{errors.cliente_email}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-v2-text-muted mb-2">Teléfono *</label>
                <input type="tel" value={formData.cliente_telefono} onChange={(e) => handleFieldChange('cliente_telefono', e.target.value)}
                  className={`w-full px-4 py-3 bg-v2-gray border rounded text-white placeholder-v2-text-muted focus:border-v2-orange focus:outline-none transition-colors ${errors.cliente_telefono ? 'border-red-500' : 'border-v2-border'}`}
                  data-testid="input-telefono" />
                {errors.cliente_telefono && <p className="text-red-400 text-xs mt-1">{errors.cliente_telefono}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-v2-text-muted mb-2">Ciudad / Estado</label>
                <input type="text" value={formData.cliente_ciudad} onChange={(e) => handleFieldChange('cliente_ciudad', e.target.value)}
                  className="w-full px-4 py-3 bg-v2-gray border border-v2-border rounded text-white placeholder-v2-text-muted focus:border-v2-orange focus:outline-none transition-colors"
                  data-testid="input-ciudad" />
              </div>
              <div>
                <label className="block text-sm font-medium text-v2-text-muted mb-2">Comentarios</label>
                <textarea rows={3} value={formData.comentarios} onChange={(e) => handleFieldChange('comentarios', e.target.value)}
                  className="w-full px-4 py-3 bg-v2-gray border border-v2-border rounded text-white placeholder-v2-text-muted focus:border-v2-orange focus:outline-none resize-none transition-colors"
                  placeholder="Ej: Necesito entrega para el viernes" data-testid="input-comentarios" />
              </div>
            </form>
          )}

          {step === 'success' && (
            <div className="text-center py-16">
              <div className="w-24 h-24 bg-v2-orange/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-v2-orange" />
              </div>
              <h3 className="text-white font-bold text-2xl mb-3">¡Cotización Enviada!</h3>
              <p className="text-v2-text-muted mb-2">Tu folio de cotización es:</p>
              <p className="text-v2-orange font-bold text-2xl font-mono mb-4">{folio}</p>
              <p className="text-v2-text-muted text-sm">Nos pondremos en contacto contigo pronto.</p>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        {step === 'cart' && items.length > 0 && (
          <div className="p-5 border-t border-v2-border bg-v2-darker">
            <div className="flex items-center justify-between mb-4">
              <span className="text-v2-text-muted">{items.length} producto{items.length > 1 ? 's' : ''}</span>
              <button onClick={clearCart} className="text-red-400 hover:text-red-300 text-sm font-medium transition-colors" data-testid="clear-cart-btn">
                Vaciar cotizador
              </button>
            </div>
            <button onClick={() => setStep('form')}
              className="w-full bg-v2-orange hover:bg-v2-orange-dark text-white font-bold uppercase tracking-wider py-4 transition-colors flex items-center justify-center gap-3"
              style={{ clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%)' }}
              data-testid="continue-to-form-btn">
              <Send size={18} /> Solicitar Cotización
            </button>
          </div>
        )}

        {step === 'form' && (
          <div className="p-5 border-t border-v2-border bg-v2-darker space-y-3">
            <button onClick={() => setStep('cart')} className="w-full border border-v2-border text-v2-text-muted hover:border-v2-orange hover:text-white py-3 transition-colors flex items-center justify-center gap-2">
              <ArrowLeft size={16} /> Volver al cotizador
            </button>
            <button onClick={handleSubmit} disabled={loading}
              className="w-full bg-v2-orange hover:bg-v2-orange-dark text-white font-bold uppercase tracking-wider py-4 transition-colors flex items-center justify-center gap-3 disabled:opacity-50"
              style={{ clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%)' }}
              data-testid="submit-quote-btn">
              {loading ? 'Enviando...' : 'Enviar Cotización'}
            </button>
          </div>
        )}

        {step === 'success' && (
          <div className="p-5 border-t border-v2-border bg-v2-darker">
            <button onClick={handleClose}
              className="w-full bg-v2-orange hover:bg-v2-orange-dark text-white font-bold uppercase tracking-wider py-4 transition-colors"
              style={{ clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%)' }}>
              Cerrar
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default QuoteDrawerV2;
